import React, { useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  AlertTitle,
  Chip,
  colors,
  DialogContent,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import UpdateIcon from '@mui/icons-material/Update';
import { alpha } from '@mui/material/styles';
import {
  BulkUpdateDefaultRatesResponse,
  DefaultRateFieldPlan,
  DefaultRateRowPlan,
  DefaultRateUnitPlan,
} from 'api/consumablesApi';
import theme from 'theme';
import { currencyType } from 'api/organizationApi';

interface Props {
  response: BulkUpdateDefaultRatesResponse;
}

const formatMoney = (value: number) => {
  const num = Number(value);
  return Number.isFinite(num) ? `${currencyType}${num.toFixed(4)}` : '—';
};

const DateBadge = ({
  dateStatus,
}: {
  dateStatus: DefaultRateFieldPlan['dateStatus'];
}) => {
  if (dateStatus === 'past') {
    return <Chip size="small" label="Past" color="warning" sx={{ ml: 1 }} />;
  }
  if (dateStatus === 'future') {
    return <Chip size="small" label="Future" color="info" sx={{ ml: 1 }} />;
  }
  return null;
};

const DateCell = ({ field }: { field?: DefaultRateFieldPlan }) => {
  if (!field) return <TableCell>—</TableCell>;
  return (
    <TableCell>
      {field.effectiveFrom}
      <DateBadge dateStatus={field.dateStatus} />
    </TableCell>
  );
};

const RateCell = ({ field }: { field?: DefaultRateFieldPlan }) => {
  if (!field) return <TableCell>—</TableCell>;

  // Date-only moves only show the date change, nothing changed on the rate itself
  if (field.action === 'unchanged' || field.dateMoved) {
    return <TableCell>{formatMoney(field.newValue)}</TableCell>;
  }
  // Newly added rate, no old value to show an arrow from
  if (field.oldValue === undefined) {
    return (
      <TableCell>
        <Typography component="span" variant="body2" sx={{ fontWeight: 700 }}>
          {formatMoney(field.newValue)}
        </Typography>
      </TableCell>
    );
  }
  return (
    <TableCell>
      {formatMoney(field.oldValue)} →{' '}
      <Typography component="span" variant="body2" sx={{ fontWeight: 700 }}>
        {formatMoney(field.newValue)}
      </Typography>
    </TableCell>
  );
};

const UnitCell = ({ unit }: { unit?: DefaultRateUnitPlan }) => {
  if (!unit) return <TableCell>—</TableCell>;
  if (unit.action === 'update') {
    return (
      <TableCell>
        {unit.oldValue} →{' '}
        <Typography component="span" variant="body2" sx={{ fontWeight: 700 }}>
          {unit.newValue}
        </Typography>
      </TableCell>
    );
  }
  return <TableCell>{unit.newValue}</TableCell>;
};

const TABLE_HEADER_SX = {
  backgroundColor: colors.blue[50],
  '& th': { color: 'common.black', fontWeight: 'bold' },
};

const BorderedTable = ({ children }: { children: React.ReactNode }) => (
  <TableContainer
    component={Paper}
    elevation={0}
    sx={{
      borderRadius: '12px',
      border: '1px solid',
      borderColor: colors.grey[300],
    }}>
    {children}
  </TableContainer>
);

const ROWS_PER_PAGE_OPTIONS = [10, 25, 50];

const RowsTable = ({ rows }: { rows: DefaultRateRowPlan[] }) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(ROWS_PER_PAGE_OPTIONS[0]);

  return (
    <BorderedTable>
      <Table size="small">
        <TableHead sx={TABLE_HEADER_SX}>
          <TableRow>
            <TableCell>Consumable</TableCell>
            <TableCell>Unit</TableCell>
            <TableCell>Buy Rate</TableCell>
            <TableCell>Buy Effective Date</TableCell>
            <TableCell>Sell Rate</TableCell>
            <TableCell>Sell Effective Date</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows
            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
            .map((row) => (
              <TableRow key={`${row.row}-${row.name}`}>
                <TableCell>{row.name}</TableCell>
                <UnitCell unit={row.unit} />
                <RateCell field={row.buy} />
                <DateCell field={row.buy} />
                <RateCell field={row.sell} />
                <DateCell field={row.sell} />
              </TableRow>
            ))}
        </TableBody>
      </Table>
      <TablePagination
        component="div"
        count={rows.length}
        page={page}
        onPageChange={(_e, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
      />
    </BorderedTable>
  );
};

const PaginatedRowsTable = ({
  columns,
  rows,
}: {
  columns: string[];
  rows: { row: number; name?: string; message: string }[];
}) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(ROWS_PER_PAGE_OPTIONS[0]);

  return (
    <BorderedTable>
      <Table size="small">
        <TableHead sx={TABLE_HEADER_SX}>
          <TableRow>
            {columns.map((column) => (
              <TableCell key={column}>{column}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows
            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
            .map((entry, index) => (
              <TableRow key={index}>
                <TableCell>{entry.row}</TableCell>
                <TableCell>{entry.name || '-'}</TableCell>
                <TableCell>{entry.message}</TableCell>
              </TableRow>
            ))}
        </TableBody>
      </Table>
      <TablePagination
        component="div"
        count={rows.length}
        page={page}
        onPageChange={(_e, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
      />
    </BorderedTable>
  );
};

const SECTION_ICONS: Record<
  'success' | 'info' | 'warning' | 'error',
  React.ElementType
> = {
  success: CheckCircleOutlineIcon,
  info: UpdateIcon,
  warning: ReportProblemOutlinedIcon,
  error: ErrorOutlineIcon,
};

const Section = ({
  title,
  count,
  severity,
  defaultExpanded,
  children,
}: {
  title: string;
  count: number;
  severity: 'success' | 'info' | 'warning' | 'error';
  defaultExpanded: boolean;
  children: React.ReactNode;
}) => {
  if (count === 0) return null;
  const paletteColor = theme.palette[severity];
  const Icon = SECTION_ICONS[severity];
  return (
    <Accordion
      defaultExpanded={defaultExpanded}
      disableGutters
      sx={{
        boxShadow: 'none',
        '&:before': { display: 'none', borderRadius: '4px' },
      }}>
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{ backgroundColor: alpha(paletteColor.main, 0.12) }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Icon fontSize="small" sx={{ color: paletteColor.main }} />
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 'bold', color: paletteColor.dark }}>
            {title} ({count})
          </Typography>
        </Stack>
      </AccordionSummary>
      <AccordionDetails>{children}</AccordionDetails>
    </Accordion>
  );
};

const BulkUpdateDefaultRatesResultView = ({ response }: Props) => {
  return (
    <DialogContent sx={{ p: 0 }}>
      <Stack direction="column" spacing={2} sx={{ p: 2 }}>
        <Alert severity={response.committed ? 'success' : 'info'}>
          <AlertTitle>
            {response.committed
              ? response.errorCount > 0
                ? 'Import Completed (Errors Ignored)'
                : 'Import completed'
              : 'Import Preview'}
          </AlertTitle>
          {response.committed
            ? `Out of ${response.totalRows} rows you have provided, ${response.createdCount} consumables were created, ${response.updatedCount} consumables had their units, rates, or effective dates updated, ${response.ignoredCount} consumables were ignored, and found ${response.errorCount} error(s) in your given list.`
            : `Out of ${response.totalRows} rows you have provided, ${response.createdCount} consumables will be created, ${response.updatedCount} consumables will update their units, rates, or effective dates, ${response.ignoredCount} consumables will be ignored, and found ${response.errorCount} error(s) in your given list.`}
        </Alert>

        <Section
          title={response.committed ? 'Created' : 'To Be Created'}
          count={response.createdCount}
          severity="success"
          defaultExpanded={
            response.committed ||
            (response.errorCount === 0 && response.updatedCount === 0)
          }>
          <RowsTable rows={response.created} />
        </Section>

        <Section
          title={response.committed ? 'Updated' : 'To Be Updated'}
          count={response.updatedCount}
          severity="info"
          defaultExpanded={response.committed || response.errorCount === 0}>
          <RowsTable rows={response.updated} />
        </Section>

        <Section
          title={response.committed ? 'Ignored' : 'To Be Ignored'}
          count={response.ignoredCount}
          severity="warning"
          defaultExpanded={false}>
          <PaginatedRowsTable
            columns={['Row', 'Consumable', 'Reason']}
            rows={response.ignored.map((row) => ({
              row: row.row,
              name: row.name,
              message: row.reason,
            }))}
          />
        </Section>

        <Section
          title="Errors"
          count={response.committed ? 0 : response.errorCount}
          severity="error"
          defaultExpanded>
          {response.errorCount > 0 && (
            <Alert severity="warning" sx={{ marginBottom: '10px' }}>
              Rows listed under "Errors" won't be created or updated, they are
              ignored entirely. Fix the reasons shown and re-upload to include
              them, or continue and they'll be left out of this import.
            </Alert>
          )}
          <PaginatedRowsTable
            columns={['Row', 'Consumable', 'Message']}
            rows={response.errors.map((error) => ({
              row: error.row,
              name: error.name,
              message: error.message,
            }))}
          />
        </Section>
      </Stack>
    </DialogContent>
  );
};

export default BulkUpdateDefaultRatesResultView;
